import {getCookie} from '@wandb/weave/common/util/cookie';
import React, {useEffect} from 'react';

import NoMatch from '../components/NoMatch';
import {startDatadogRumView} from '../util/datadog';
import {trackPage} from '../util/navigation';
import {isInIframe} from '../util/window';

const HOST_SESSION_ID_COOKIE = `host_session_id`;

export type RouteWithLayoutProps = {
  component: React.ComponentType<any>;
  routeName?: string;
  error?: any;
  allowIframes?: boolean;
  additionalProps?: {[prop: string]: any};
  [prop: string]: any;
};

// adds the Nav and Footer before and after the route component
export const RouteWithLayout = ({
  component: Component,
  routeName,
  error,
  allowIframes,
  additionalProps = {},
  ...rest
}: RouteWithLayoutProps) => {
  const tab = rest?.computedMatch?.params?.tab ?? additionalProps?.tab;
  const tabSuffix = tab ? `:${tab}` : '';
  useRouteTracking(routeName, tabSuffix, tab);
  usePageViewTracking(routeName, tab, tabSuffix);
  const preventIframe = isInIframe() && !allowIframes;
  if (preventIframe) {
    return <NoMatch unrecoverable />;
  }
  if (!Component) {
    return null;
  }
  return <Component {...rest} tab={tab} error={error} />;
};

const usePageViewTracking = (
  routeName: string | undefined,
  tab: string | undefined,
  tabSuffix: string
) => {
  useEffect(() => {
    const options = {
      context: {
        hostSessionID: getCookie(HOST_SESSION_ID_COOKIE),
      },
    };

    trackPage(
      {
        routeName,
        tab,
        routeAndTab: routeName + tabSuffix,
        isNew: true,
      },
      options
    );
  }, [routeName, tab, tabSuffix]);
};

const useRouteTracking = (
  routeName: string | undefined,
  tabSuffix: string,
  tab: string | undefined
) => {
  useEffect(() => {
    const routeNameWithTab = routeName + tabSuffix;
    startDatadogRumView(routeNameWithTab);

    // Why does this need clean up? If the route we're going to
    // isn't a RouteWithLayout, it won't call updateProfilerRoute.
    // So, by defining a cleanup that clears the route name, we ensure
    // that the profiler is updated to not have a route name when the
    // user leaves the page.
    // There are few pages that don't use RouteWithLayout, so this is
    // mostly for future-proofing.
    return () => {
      startDatadogRumView(routeNameWithTab);
    };
  }, [routeName, tab, tabSuffix]);
};
